import { ChangeEvent, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { ROLE_ACTIONS } from "components/Actions/types";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import QueryDropdown from "components/QueryDropdown";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import { getAllPermissions } from "context/auth";
import { PERMISSIONS } from "context/auth/types";
import ParamsContext from "context/params/paramsContext";
import {
    DROPDOWN_SIZES,
    ENDPOINTS,
    FILTER_NAMES,
    ID_TYPE,
    Query,
    TABLE_NAMES,
    TRUE,
    useApi,
    useStringInputValidation
} from "shared";

import DialogActions from "../../components/DialogActions";
import PermissionList from "./PermissionList";
import { Data, FIELD_NAMES, ManageRoleProps } from "./types";

const ManageRole = ({ action, rowData, isOpen, close }: ManageRoleProps) => {
    const { setReloadItems } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { postData, updateData, handleResponse } = useApi();

    const [isLoading, setIsLoading] = useState(false);
    const [isLocalPermissionCheck, setIsLocalPermissionCheck] = useState(false);

    const [data, setData] = useState<Data>({
        [FIELD_NAMES.RoleName]: "",
        [FIELD_NAMES.Company]: null,
        [FIELD_NAMES.Description]: "",
        [FIELD_NAMES.PermissionPreset]: null,
        [FIELD_NAMES.Permissions]: []
    });

    const { roleName, company, description, permissions } = data;

    const { stringInputError: roleNameError } =
        useStringInputValidation(roleName);

    const { stringInputError: descriptionError } =
        useStringInputValidation(description);

    const isCreateRole = action === ROLE_ACTIONS.CreateRole;

    useEffect(() => {
        if (rowData) {
            const { name, company, description, permissions } = rowData;

            setData(prev => ({
                ...prev,
                [FIELD_NAMES.RoleName]: name,
                [FIELD_NAMES.Company]: company,
                [FIELD_NAMES.Description]: description,
                [FIELD_NAMES.Permissions]: permissions
            }));
        }
    }, [rowData]);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const payload = {
                name: roleName,
                description,
                permissions,
                ...(isCreateRole ? { company_id: company?.id } : {})
            };

            const response = await (isCreateRole
                ? postData(ENDPOINTS.Roles, payload)
                : updateData(ENDPOINTS.Roles, rowData?.id, payload));

            handleCloseDialog();
            isCreateRole && setReloadItems(TABLE_NAMES.Roles);
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    const handleCloseDialog = () =>
        close(isCreateRole ? ROLE_ACTIONS.CreateRole : ROLE_ACTIONS.EditRole);

    const handleInputFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    const handleCompanyChange = (value: Query | null) =>
        setData(prev => ({ ...prev, [FIELD_NAMES.Company]: value }));

    const handlePermissionPresetChange = (value: Query | null) => {
        const getUpdatedPermissions = () => {
            const rolePermissions = value?.permissions;

            if (rolePermissions) {
                const allPermissions = getAllPermissions();

                return allPermissions.filter(perm =>
                    rolePermissions.includes(perm)
                );
            } else {
                return permissions;
            }
        };

        const updatedPermissions = getUpdatedPermissions();

        setData(prev => ({
            ...prev,
            [FIELD_NAMES.PermissionPreset]: value,
            [FIELD_NAMES.Permissions]: updatedPermissions
        }));
    };

    const handlePermissionCheck = (
        activePermissions: PERMISSIONS[],
        isLocalCheck?: boolean
    ) => {
        setIsLocalPermissionCheck(Boolean(isLocalCheck));

        // add or remove permissions from the list
        setData(prev => {
            const prevPermissions = prev[FIELD_NAMES.Permissions];

            const updatedPermissions = activePermissions.reduce(
                (currentPermissions, activePermission) => {
                    const hasPermission =
                        currentPermissions.includes(activePermission);

                    return hasPermission
                        ? currentPermissions.filter(
                              currentPerm => currentPerm !== activePermission
                          )
                        : [...currentPermissions, activePermission];
                },
                prevPermissions
            );

            return {
                ...prev,
                [FIELD_NAMES.Permissions]: updatedPermissions
            };
        });
    };

    return (
        <Dialog
            title={
                isCreateRole
                    ? t("Dialog##create role")
                    : t("General##edit role")
            }
            description={
                isCreateRole
                    ? t("Dialog##create role description")
                    : t("Dialog##edit role description")
            }
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={handleCloseDialog}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setData({
                            [FIELD_NAMES.RoleName]: "",
                            [FIELD_NAMES.Company]: null,
                            [FIELD_NAMES.Description]: "",
                            [FIELD_NAMES.PermissionPreset]: null,
                            [FIELD_NAMES.Permissions]: []
                        });
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading}
                    isReadyToConfirm={
                        roleName &&
                        !roleNameError &&
                        !descriptionError &&
                        company
                    }
                    confirmationButtonText={
                        isCreateRole ? t("Button##create") : t("Button##save")
                    }
                />
            }
            extendToMaxWidth
        >
            <InputField
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                required
                name={FIELD_NAMES.RoleName}
                value={roleName}
                readOnly={isLoading}
                errorText={roleNameError}
                onChange={handleInputFieldChange}
                labelLeft={t("Dialog##role name")}
                placeholder={t("Dialog##enter name")}
                customStyle={{ marginBottom: "16px" }}
            />

            <CompanyDropdown
                isRequired={isCreateRole}
                value={company}
                readOnly={isLoading || !isCreateRole}
                change={(_, val) => handleCompanyChange(val)}
                customStyle={{ marginBottom: "16px" }}
                isRootRequired
            />

            <InputField
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                name={FIELD_NAMES.Description}
                value={description}
                readOnly={isLoading}
                errorText={descriptionError}
                onChange={handleInputFieldChange}
                labelLeft={`${t("Dialog##description")} (${t(
                    "Dialog##optional"
                )})`}
                placeholder={t("Dialog##enter description")}
                customStyle={{ marginBottom: "16px" }}
            />

            <QueryDropdown
                resource={`${ENDPOINTS.Roles}?${FILTER_NAMES.IncludeStatic}=${TRUE}`}
                idType={ID_TYPE.Id}
                size={DROPDOWN_SIZES.Medium}
                fullWidth
                readOnly={isLoading}
                change={(_, val) => handlePermissionPresetChange(val)}
                labelLeft={`${t("Dialog##permission preset")} (${t(
                    "Dialog##optional"
                )})`}
                textFieldParams={{ placeholder: t("Dialog##select") }}
                customStyle={{ marginBottom: "16px" }}
            />

            <PermissionList
                checkedPermissions={permissions}
                isLocalPermissionCheck={isLocalPermissionCheck}
                handlePermissionCheck={handlePermissionCheck}
            />
        </Dialog>
    );
};

export default ManageRole;
